import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getNavTheme } from '../../../navigation/clinicNavConfig';
import Pagination from '../../common/Pagination';
import BookingPatientCard from './BookingPatientCard';
import WebBookingsSkeleton from './subparts/WebBookingsSkeleton';
import WebBookingsIcon from './subparts/WebBookingsIcon';
import bookingService from '../../../services/booking.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import {
    buildBookingRequestParams,
    DEFAULT_WEB_BOOKING_SEARCH,
    WEB_BOOKING_SEARCH_COLUMNS,
    WEB_BOOKING_TABS,
} from './subparts/webBookingsQuery';
import { WEB_BOOKINGS_PAGE_THEME } from './subparts/webBookingsTheme';

const PAGE_SIZE = 10;

function BookingRequests({ bookingStatus, tabId, tabLabel, refreshToken, onCountUpdate }) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [loading, setLoading] = useState(true);
    const [patientRequests, setPatientRequests] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [filters, setFilters] = useState(DEFAULT_WEB_BOOKING_SEARCH);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_WEB_BOOKING_SEARCH);
    const onCountUpdateRef = useRef(onCountUpdate);

    const activeTab = WEB_BOOKING_TABS.find((tab) => tab.id === tabId);

    useEffect(() => {
        onCountUpdateRef.current = onCountUpdate;
    }, [onCountUpdate]);

    const fetchBookingRequests = useCallback(
        async (page, search) => {
            if (!clinicId) {
                return;
            }

            setLoading(true);

            try {
                const params = buildBookingRequestParams(bookingStatus, search);
                const res = await bookingService.getBookingRequests(clinicId, page, PAGE_SIZE, params);
                const data = res.data || {};

                setPatientRequests(data.content || []);
                setTotalPages(data.totalPages || 0);
                setTotalElements(data.totalElements || 0);

                if (tabId === 'new') {
                    onCountUpdateRef.current?.('new', data.totalElements || 0);
                }
            } catch (error) {
                console.error('Error fetching booking requests:', error);
                toast.error('Failed to load booking requests');
            } finally {
                setLoading(false);
            }
        },
        [bookingStatus, clinicId, tabId]
    );

    useEffect(() => {
        fetchBookingRequests(pageNo, appliedFilters);
    }, [fetchBookingRequests, pageNo, appliedFilters, refreshToken]);

    const handleSearch = (event) => {
        event.preventDefault();
        setPageNo(0);
        setAppliedFilters(filters);
    };

    const handleClearSearch = () => {
        setFilters(DEFAULT_WEB_BOOKING_SEARCH);
        setAppliedFilters(DEFAULT_WEB_BOOKING_SEARCH);
        setPageNo(0);
    };

    const handleColumnChange = (column) => {
        setFilters((prev) => ({ ...prev, column }));
    };

    const handlePageChange = (newPage) => {
        setPageNo(newPage);
    };

    const handleUpdate = () => {
        fetchBookingRequests(pageNo, appliedFilters);
    };

    return (
        <>
            <div className="web-bookings-filters">
                <form onSubmit={handleSearch}>
                    <div className="web-bookings-filters__row">
                        <div className="web-bookings-filters__search input-group input-group-sm">
                            <span className="input-group-text web-bookings-filters__search-icon bg-white border-end-0">
                                <WebBookingsIcon icon="bi-search" theme={WEB_BOOKINGS_PAGE_THEME} size="xs" />
                            </span>
                            <input
                                type="text"
                                className="form-control form-control-sm border-start-0"
                                placeholder="Search booking requests..."
                                value={filters.value}
                                onChange={(event) => setFilters((prev) => ({ ...prev, value: event.target.value }))}
                                disabled={loading}
                            />
                            <button type="submit" className="btn btn-sm btn-accent web-bookings-filters__search-btn" disabled={loading}>
                                <i className="bi bi-search me-1" />
                                Search
                            </button>
                        </div>

                        {appliedFilters.value && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ecx-modal__close-btn"
                                onClick={handleClearSearch}
                                disabled={loading}
                            >
                                <i className="bi bi-x-lg me-1" />
                                Clear
                            </button>
                        )}
                    </div>

                    <div className="web-bookings-filters__row web-bookings-filters__row--pills">
                        <span className="web-bookings-filters__label">Search By</span>
                        <div className="web-bookings-pills">
                            {WEB_BOOKING_SEARCH_COLUMNS.map((option) => {
                                const isActive = filters.column === option.value;
                                const theme = getNavTheme(option.theme);

                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`web-bookings-pill ${
                                            isActive ? `${theme.activeClass} is-active` : theme.idleClass
                                        }`}
                                        onClick={() => handleColumnChange(option.value)}
                                        disabled={loading}
                                    >
                                        <WebBookingsIcon icon={option.icon} theme={option.theme} size="xs" />
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </form>
            </div>

            <div className="web-bookings-toolbar">
                <div className="web-bookings-toolbar__title-wrap">
                    <WebBookingsIcon
                        icon={activeTab?.icon || 'bi-inbox'}
                        theme={activeTab?.theme || WEB_BOOKINGS_PAGE_THEME}
                        size="sm"
                    />
                    <h2 className="web-bookings-toolbar__title">{tabLabel}</h2>
                </div>
                <div className="web-bookings-toolbar__meta">
                    <span className="web-bookings-chip">
                        <WebBookingsIcon icon="bi-inbox" theme={activeTab?.theme || WEB_BOOKINGS_PAGE_THEME} size="xs" />
                        {totalElements} request{totalElements === 1 ? '' : 's'}
                    </span>
                    {totalPages > 0 && (
                        <span className="web-bookings-chip">
                            <WebBookingsIcon icon="bi-layers" theme="purple" size="xs" />
                            Page {pageNo + 1} of {Math.max(totalPages, 1)}
                        </span>
                    )}
                </div>
            </div>

            <div className="web-bookings-list ecx-page-scroll">
                {loading ? (
                    <WebBookingsSkeleton count={3} />
                ) : patientRequests.length > 0 ? (
                    <div className="web-bookings-list__items">
                        {patientRequests.map((patient, index) => (
                            <BookingPatientCard
                                key={patient.id}
                                request={patient}
                                index={index}
                                handleUpdate={handleUpdate}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="ecx-page-empty web-bookings-empty">
                        <WebBookingsIcon
                            icon="bi-calendar-check"
                            theme={activeTab?.theme || WEB_BOOKINGS_PAGE_THEME}
                            size="xl"
                            className="web-bookings-empty__icon mb-3"
                        />
                        <h3 className="h6 fw-normal text-dark mb-1">No requests found</h3>
                        <p className="mb-0 small">
                            {appliedFilters.value
                                ? 'Try adjusting your search filters.'
                                : 'Booking requests for this section will appear here.'}
                        </p>
                    </div>
                )}
            </div>

            {!loading && totalPages > 1 && (
                <div className="ecx-page-pagination web-bookings-pagination border-top p-3">
                    <Pagination
                        currentPage={pageNo}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalElements={totalElements}
                        pageSize={PAGE_SIZE}
                    />
                </div>
            )}
        </>
    );
}

export default BookingRequests;
